import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setTokens } from "@/api";
import { ZeusBolt } from "@/components/ZeusBolt";
import { toast } from "sonner";

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get("payment") === "success";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setPaymentRequired(false);

    try {
      if (isLogin) {
        const res = await api.post<{ token: string; refreshToken?: string }>(
          "/auth/login",
          { email, password }
        );
        if (res.ok && res.data?.token) {
          setTokens(res.data.token, res.data.refreshToken);
          navigate("/");
        } else {
          toast.error(res.error || "Invalid email or password");
        }
      } else {
        const res = await api.post<{ code?: string }>("/auth/register", { email, password });
        if (res.ok) {
          toast.success("Account created! You can now sign in.");
          setIsLogin(true);
        } else if (res.status === 403) {
          setPaymentRequired(true);
        } else {
          toast.error(res.error || "Registration failed");
        }
      }
    } catch {
      toast.error("Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-2xl gold-gradient flex items-center justify-center shadow-lg shadow-primary/20">
            <ZeusBolt size={28} className="text-primary-foreground" />
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isLogin ? "Sign in to Zeus" : "Get started with Zeus"}
          </p>
        </div>

        {paymentSuccess && !isLogin && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-center">
            <p className="text-sm font-medium text-green-400">Payment received!</p>
            <p className="text-xs text-muted-foreground mt-1">
              Create your account below using the same email you paid with.
            </p>
          </div>
        )}

        {paymentRequired && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-center">
            <p className="text-sm font-medium text-amber-400">Payment required</p>
            <p className="text-xs text-muted-foreground mt-1">
              You need to purchase a plan before creating an account.
            </p>
            <a
              href="https://opulentbots.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-3 px-4 py-2 rounded-lg gold-gradient text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity"
            >
              Get Started at OpulentBots.com
            </a>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-foreground" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-secondary text-foreground text-sm border border-border placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring transition-colors"
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm text-foreground" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-secondary text-foreground text-sm border border-border placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring transition-colors"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg gold-gradient text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Loading\u2026" : isLogin ? "Sign in" : "Sign up"}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => { setIsLogin(!isLogin); setPaymentRequired(false); }}
            className="text-primary hover:underline"
          >
            {isLogin ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
